import React from 'react'
import { Switch, SwitchProps, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react'
import { Spin } from 'antd'
import { Container } from 'typescript-ioc/es5'
import { AppState } from '../../stores/app'
import { SessionState } from '@pushly/aqe'
import { AccessUnavailable } from '../../core/access/unavailable-screen'
import type { AccessContext } from '../../core/access/resolve-access'
import { retryAccess } from '../../core/access/open-access-path'
import { keyOf } from '../../core/access/access-owner'
import { parseAccessUrl } from '../../core/access/url'
import { BootFailed } from '../../core/boot/boot-screens'
import { errorReferenceId } from '../../_utils/airbrake'

interface AppSwitchProps extends SwitchProps {
    handle404?: boolean
}

/** The path prefix that names a context, so a resolved context and a URL are compared by one key. */
const pathOf = (context: AccessContext): string =>
    context.kind === 'domain' ? `/domains/${context.domainId}` : `/organizations/${context.orgId}`

/**
 * What the route area shows when the access owner's answer is not the open domain for the URL. A URL
 * that names a domain or organization is never rendered against another domain's data: while the
 * owner resolves it the area holds a placeholder, and when the resolution failed it shows the failure
 * with Retry. The chrome around the area stays. A switch started through `openAccessPath` keeps the
 * URL on the open domain until it resolves, so it never reaches the placeholder.
 */
const AccessRouteGuard = observer(({ children }: { children: React.ReactNode }) => {
    const appState = Container.get(AppState)
    const owner = appState.accessOwner
    const { pathname } = useLocation()
    const url = parseAccessUrl(pathname)
    const resolution = owner.resolution

    if (url.kind !== 'none') {
        if (resolution.status === 'failed') {
            return (
                <BootFailed
                    tier="access"
                    kind={resolution.kind}
                    referenceId={errorReferenceId(resolution.error)}
                    onRetry={() => retryAccess(owner)}
                />
            )
        }
        if (resolution.status === 'resolving') {
            const previous = resolution.previous
            const answered = previous !== undefined && keyOf(parseAccessUrl(pathOf(previous.context))) === keyOf(url)
            if (!answered) {
                return (
                    <div
                        data-testid="access-switching"
                        role="status"
                        aria-busy="true"
                        style={{ textAlign: 'center', paddingTop: '20vh' }}
                    >
                        <Spin size="large" />
                    </div>
                )
            }
        }
    }

    return <>{children}</>
})

@observer
export class AppSwitch extends React.Component<AppSwitchProps> {
    private appState: AppState
    private sessionState: SessionState

    public constructor(props: AppSwitchProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.sessionState = Container.get(SessionState)
    }

    public render() {
        const { handle404, children, ...switchProps } = this.props

        if (handle404 && this.sessionState.isAuthenticated && this.appState.showing404Count > 0) {
            if (this.appState.appIsLoading) {
                return null
            }
            return <AccessUnavailable />
        }

        const routes = <Switch {...switchProps}>{children}</Switch>
        return handle404 ? <AccessRouteGuard>{routes}</AccessRouteGuard> : routes
    }
}
